/**
 * Notification Bell Management System
 * Handles notification count display and interactions
 */

interface NotificationAPI {
  setCount(count: number): void;
  addNotification(message: string): void;
  getCount(): number;
}

class NotificationManager {
  private notificationBell: HTMLElement | null = null;
  private notificationBadge: HTMLElement | null = null;
  private notificationBellMobile: HTMLElement | null = null;
  private notificationBadgeMobile: HTMLElement | null = null;

  constructor() {
    this.init();
  }

  /**
   * Initialize notification system
   */
  private init(): void {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
      return;
    }

    // Get DOM elements
    this.notificationBell = document.getElementById('notification-bell');
    this.notificationBadge = document.getElementById('notification-count');
    this.notificationBellMobile = document.getElementById('notification-bell-mobile');
    this.notificationBadgeMobile = document.getElementById('notification-count-mobile');

    if (!this.notificationBell && !this.notificationBellMobile) {
      return; // User not authenticated or elements not found
    }

    // Set up event listeners
    this.setupEventListeners();
    
    // Update badge visibility
    this.updateBadgeVisibility();
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    // Desktop notification bell
    if (this.notificationBell) {
      this.notificationBell.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleNotificationClick();
      });

      this.notificationBell.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          this.handleNotificationClick();
        }
      });
    }

    // Mobile notification bell
    if (this.notificationBellMobile) {
      this.notificationBellMobile.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleNotificationClick();
      });

      this.notificationBellMobile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          this.handleNotificationClick();
        }
      });
    }
  }

  /**
   * Handle notification bell click
   */
  private handleNotificationClick(): void {
    // Show notification dropdown or modal
    // For now, just show an alert - replace with actual notification system
    this.showNotificationPanel();
    
    // Mark notifications as read (example)
    // markNotificationsAsRead();
  }

  /**
   * Show notification panel (placeholder)
   */
  private showNotificationPanel(): void {
    // This could be a dropdown, modal, or redirect to notifications page
    alert('Sistema notifiche in sviluppo!\n\nFunzionalità future:\n• Notifiche tornei\n• Promemoria partite\n• Aggiornamenti profilo');
    
    // Demo: simulate marking as read
    setTimeout(() => {
      this.setNotificationCount(0);
    }, 2000);
  }

  /**
   * Set notification count
   */
  public setNotificationCount(count: number): void {
    // Update desktop badge
    if (this.notificationBadge) {
      this.notificationBadge.textContent = count.toString();
      this.notificationBadge.setAttribute('data-count', count.toString());
    }
    
    // Update mobile badge
    if (this.notificationBadgeMobile) {
      this.notificationBadgeMobile.textContent = count.toString();
      this.notificationBadgeMobile.setAttribute('data-count', count.toString());
    }
    
    // Update accessibility
    const bellLabel = count > 0 ? 
      `Notifiche (${count} non lette)` : 
      'Notifiche (nessuna nuova)';
    
    if (this.notificationBell) {
      this.notificationBell.setAttribute('aria-label', bellLabel);
      this.notificationBell.setAttribute('title', bellLabel);
    }
    
    if (this.notificationBellMobile) {
      this.notificationBellMobile.setAttribute('aria-label', bellLabel);
      this.notificationBellMobile.setAttribute('title', bellLabel);
    }
    
    this.updateBadgeVisibility();
  }

  /**
   * Update badge visibility based on count
   */
  private updateBadgeVisibility(): void {
    // Update desktop badge
    if (this.notificationBadge) {
      const count = parseInt(this.notificationBadge.getAttribute('data-count') || '0');
      this.notificationBadge.style.display = count === 0 ? 'none' : 'flex';
    }
    
    // Update mobile badge
    if (this.notificationBadgeMobile) {
      const count = parseInt(this.notificationBadgeMobile.getAttribute('data-count') || '0');
      this.notificationBadgeMobile.style.display = count === 0 ? 'none' : 'flex';
    }
  }

  /**
   * Add new notification (example function)
   */
  public addNotification(message: string): void {
    const currentCount = parseInt(
      (this.notificationBadge || this.notificationBadgeMobile)?.getAttribute('data-count') || '0'
    );
    this.setNotificationCount(currentCount + 1);
    
    // Could store notification in localStorage or send to backend
    console.log('New notification:', message);
  }

  /**
   * Get current notification count
   */
  public getNotificationCount(): number {
    return parseInt((this.notificationBadge || this.notificationBadgeMobile)?.getAttribute('data-count') || '0');
  }
}

// Initialize and expose API
const notificationManager = new NotificationManager();

// Public API
const notifications: NotificationAPI = {
  setCount: (count: number) => notificationManager.setNotificationCount(count),
  addNotification: (message: string) => notificationManager.addNotification(message),
  getCount: () => notificationManager.getNotificationCount()
};

// Extend global window object
declare global {
  interface Window {
    PadelMatcherNotifications: NotificationAPI;
  }
}

window.PadelMatcherNotifications = notifications;

// Debug info
if (window.location.search.includes('debug=notifications')) {
  console.log('PadelMatcher Notifications initialized');
}

export default notifications;
export type { NotificationAPI };